import { useState, useCallback, useRef, useEffect } from "react";
import { useI18n } from "@/i18n";
import Papa from "papaparse";
import { importProductsCsv } from "@/infrastructure/product/productService";
import { listTypeUnits } from "@/infrastructure/type-unit/typeUnitService";
import type { TypeUnit } from "@/domain/types";
import { colors, typography, transitions } from "@/design-system/tokens";

const MAX_ROWS = 50_000;
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const TARGET_FIELDS = ["code", "name", "description"] as const;

const FIELD_LABELS: Record<string, string> = {
  code: "Code", name: "Name", description: "Description", ignore: "— Ignore —",
};

function suggestMapping(header: string): string {
  const h = header.toLowerCase().trim();
  if (h.includes("referencia") || h.includes("codigo") || h.includes("code") || h.includes("sku") || h.includes("ref")) return "code";
  if (h.includes("nombre") || h.includes("name") || h.includes("producto") || h.includes("product")) return "name";
  if (h.includes("descripcion") || h.includes("description") || h.includes("desc")) return "description";
  return "ignore";
}

export function ProductImportModal({ onClose, onDone }: { onClose: () => void; onDone: (jobId: string) => void }) {
  const { t } = useI18n();
  const fileRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<"file" | "map" | "processing" | "done" | "error">("file");
  const [file, setFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [headers, setHeaders] = useState<string[]>([]);
  const [previewRows, setPreviewRows] = useState<Record<string, string>[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [typeUnitId, setTypeUnitId] = useState("");
  const [units, setUnits] = useState<TypeUnit[]>([]);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => { listTypeUnits().then(all => setUnits(all.filter(u => u.status === "ACTIVE"))).catch(() => {}); }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;

    if (!f.name.toLowerCase().endsWith(".csv")) {
      setErrorMsg(t.products.importErrorFormat ?? "Only CSV files are accepted.");
      setStep("error");
      return;
    }

    if (f.size > MAX_FILE_SIZE) {
      const sizeMB = (f.size / (1024 * 1024)).toFixed(1);
      setErrorMsg(t.products.importErrorSize ? t.products.importErrorSize.replace("{size}", sizeMB) : `The file weighs ${sizeMB} MB. Maximum allowed is 10 MB. Please contact support.`);
      setStep("error");
      return;
    }

    setFile(f);
    setFileName(f.name);
    Papa.parse<Record<string, string>>(f, {
      header: true, skipEmptyLines: true, preview: MAX_ROWS + 1,
      complete: (results) => {
        if (results.data.length > MAX_ROWS) { setErrorMsg(`Maximum ${MAX_ROWS.toLocaleString()} rows allowed.`); setStep("error"); return; }
        const h = results.meta.fields ?? [];
        setHeaders(h);
        setRowCount(results.data.length);
        setPreviewRows(results.data.slice(0, 4));
        const autoMap: Record<string, string> = {};
        h.forEach(col => { autoMap[col] = suggestMapping(col); });
        setMapping(autoMap);
        setStep("map");
      },
      error: () => { setErrorMsg(t.products.errorValidation); setStep("error"); },
    });
  }, [t.products.errorValidation]);

  const handleMappingChange = useCallback((col: string, field: string) => {
    setMapping(prev => ({ ...prev, [col]: field }));
  }, []);

  const codeCol = Object.entries(mapping).find(([, v]) => v === "code")?.[0];
  const nameCol = Object.entries(mapping).find(([, v]) => v === "name")?.[0];
  const canSubmit = !!codeCol && !!nameCol && !!typeUnitId && !!file;

  const handleImport = useCallback(async () => {
    if (!file || !canSubmit) return;
    setStep("processing");
    const backendMapping: Record<string, string> = {};
    Object.entries(mapping).forEach(([col, field]) => { if (field !== "ignore") backendMapping[field] = col; });

    try {
      const res = await importProductsCsv(file, typeUnitId, backendMapping);
      setStep("done");
      onDone(res.jobId);
    } catch (err) {
      const status = (err as any)?.response?.status;
      setErrorMsg(status === 500 ? (t.products.importErrorServer ?? "Server error") : t.products.errorForbidden);
      setStep("error");
    }
  }, [file, canSubmit, mapping, typeUnitId, onDone, t.products]);

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: "24px" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: "20px", padding: "32px", width: "100%", maxWidth: "560px", display: "flex", flexDirection: "column", gap: "20px", maxHeight: "90vh", overflowY: "auto" }}>
        <h2 style={{ fontFamily: typography.fontFamily, fontSize: typography.size.lg, fontWeight: typography.weight.bold, color: "#111", margin: 0 }}>{t.products.importTitle}</h2>

        {step === "file" && (
          <>
            <p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#888", margin: 0 }}>{t.products.importDescription}</p>
            <label style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#fff", background: "#111", borderRadius: "9px", padding: "10px 18px", cursor: "pointer", display: "inline-block", width: "fit-content" }}>
              {t.products.importSelectFile}
              <input ref={fileRef} type="file" accept=".csv" onChange={handleFileChange} style={{ display: "none" }} />
            </label>
            <p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#bbb", margin: 0 }}>{t.products.importMaxItems}</p>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button onClick={onClose} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#666", background: "transparent", border: "1px solid #e0e0e0", borderRadius: "9px", padding: "10px 20px", cursor: "pointer" }}>{t.products.cancel}</button>
            </div>
          </>
        )}

        {step === "map" && (
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#333" }}>{t.products.unitLabel}</span>
              <select value={typeUnitId} onChange={e => setTypeUnitId(e.target.value)} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: typeUnitId ? "#111" : "#aaa", background: "#fff", border: "1.5px solid #e5e5e5", borderRadius: "10px", padding: "10px 12px", outline: "none", width: "100%", boxSizing: "border-box" }}>
                <option value="">{t.products.unitPlaceholder}</option>
                {units.map(u => <option key={u.id} value={u.id}>{u.name} ({u.abbreviation})</option>)}
              </select>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <span style={{ fontFamily: typography.fontFamily, fontSize: "10px", fontWeight: typography.weight.bold, color: "#bbb", textTransform: "uppercase", letterSpacing: "0.1em" }}>Column Mapping</span>
              {headers.map(col => (
                <div key={col} style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#555", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{col}</span>
                  <span style={{ color: "#ccc" }}>→</span>
                  <select value={mapping[col] ?? "ignore"} onChange={e => handleMappingChange(col, e.target.value)} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, border: "1px solid #e5e5e5", borderRadius: "8px", padding: "6px 10px", outline: "none", minWidth: "140px" }}>
                    <option value="ignore">{FIELD_LABELS.ignore}</option>
                    {TARGET_FIELDS.map(f => <option key={f} value={f}>{FIELD_LABELS[f]}</option>)}
                  </select>
                </div>
              ))}
            </div>
            {canSubmit && previewRows.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <span style={{ fontFamily: typography.fontFamily, fontSize: "10px", fontWeight: typography.weight.bold, color: "#bbb", textTransform: "uppercase", letterSpacing: "0.1em" }}>Preview</span>
                <div style={{ background: "#f9f9f9", borderRadius: "8px", padding: "10px", overflowX: "auto" }}>
                  {previewRows.map((row, i) => (
                    <div key={i} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#555", padding: "3px 0", borderBottom: i < previewRows.length - 1 ? "1px solid #f0f0f0" : "none" }}>
                      <strong>{codeCol ? row[codeCol] : ""}</strong> | {nameCol ? row[nameCol] : ""}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#aaa" }}>{rowCount.toLocaleString()} rows</span>
              <div style={{ display: "flex", gap: "10px" }}>
                <button onClick={onClose} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#666", background: "transparent", border: "1px solid #e0e0e0", borderRadius: "9px", padding: "10px 20px", cursor: "pointer" }}>{t.products.cancel}</button>
                <button onClick={handleImport} disabled={!canSubmit} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#fff", background: canSubmit ? colors.primary : "#ccc", border: "none", borderRadius: "9px", padding: "10px 20px", cursor: canSubmit ? "pointer" : "default", transition: `background ${transitions.fast}` }}>
                  {t.products.importBtn} ({rowCount.toLocaleString()})
                </button>
              </div>
            </div>
          </>
        )}

        {step === "processing" && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px", padding: "32px" }}>
            <span style={{ width: "24px", height: "24px", border: `3px solid ${colors.primary}`, borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />
            <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#888" }}>{t.products.importUploading}</span>
          </div>
        )}

        {step === "done" && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "14px", padding: "24px 0" }}>
            <div style={{ width: "48px", height: "48px", background: "rgba(34,197,94,0.1)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
            </div>
            <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.base, fontWeight: typography.weight.semibold, color: "#111" }}>
              {t.products.successCreated}
            </span>
            <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#888", textAlign: "center" }}>
              {rowCount.toLocaleString()} rows submitted. You'll see the results when processing completes.
            </span>
            <button onClick={onClose} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#fff", background: "#111", border: "none", borderRadius: "9px", padding: "10px 24px", cursor: "pointer", marginTop: "4px" }}>OK</button>
          </div>
        )}

        {step === "error" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: colors.error }}>{errorMsg}</span>
            <button onClick={() => setStep("file")} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#666", background: "transparent", border: "1px solid #e0e0e0", borderRadius: "9px", padding: "10px 20px", cursor: "pointer", alignSelf: "flex-end" }}>Retry</button>
          </div>
        )}
      </div>
    </div>
  );
}
